import React from "react";
import { motion } from "framer-motion";

// MATERIAL UI COMPONENTS
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import { alpha, useTheme } from "@mui/material/styles";

// ICONS
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import StoreMallDirectoryIcon from "@mui/icons-material/StoreMallDirectory";
import RamenDiningIcon from "@mui/icons-material/RamenDining";
import LocalAtmIcon from "@mui/icons-material/LocalAtm";
import DeliveryDiningIcon from "@mui/icons-material/DeliveryDining";
import TakeoutDiningIcon from "@mui/icons-material/TakeoutDining";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import PersonIcon from "@mui/icons-material/Person";
import TableBarIcon from "@mui/icons-material/TableBar";
import CancelIcon from "@mui/icons-material/Cancel";

// Animation variants
const cardVariants = {
  hover: {
    y: -4,
    boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 20,
    },
  },
  tap: {
    scale: 0.98,
    transition: { duration: 0.1 },
  },
};

const iconVariants = {
  hover: {
    rotate: [0, -10, 10, -10, 0],
    transition: {
      duration: 0.5,
    },
  },
};

const buttonVariants = {
  hover: {
    scale: 1.05,
    transition: { duration: 0.2 },
  },
  tap: {
    scale: 0.95,
    transition: { duration: 0.1 },
  },
};

export default function CardNumeric({
  cardSize = "medium",
  label,
  value,
  secondaryValue,
  subValue,
  icon,
  action,
  colorScheme = "primary",
}) {
  const theme = useTheme();

  // Get color scheme based on prop
  const getColorScheme = () => {
    switch (colorScheme) {
      case "success":
        return {
          light: alpha(theme.palette.success.main, 0.1),
          main: theme.palette.success.main,
          dark: theme.palette.success.dark,
        };
      case "warning":
        return {
          light: alpha(theme.palette.warning.main, 0.1),
          main: theme.palette.warning.main,
          dark: theme.palette.warning.dark,
        };
      case "error":
        return {
          light: alpha(theme.palette.error.main, 0.1),
          main: theme.palette.error.main,
          dark: theme.palette.error.dark,
        };
      case "info":
        return {
          light: alpha(theme.palette.info.main, 0.1),
          main: theme.palette.info.main,
          dark: theme.palette.info.dark,
        };
      case "primary":
      default:
        return {
          light: alpha(theme.palette.primary.main, 0.1),
          main: theme.palette.primary.main,
          dark: theme.palette.primary.dark,
        };
    }
  };

  const colors = getColorScheme();

  const getIcon = () => {
    const iconProps = {
      fontSize: "small",
      sx: { color: colors.main },
    };

    switch (icon) {
      case "store":
        return <StoreMallDirectoryIcon {...iconProps} />;
      case "order":
        return <RamenDiningIcon {...iconProps} />;
      case "money":
        return <LocalAtmIcon {...iconProps} />;
      case "delivery":
        return <DeliveryDiningIcon {...iconProps} />;
      case "takeout":
        return <TakeoutDiningIcon {...iconProps} />;
      case "date":
        return <CalendarMonthIcon {...iconProps} />;
      case "cold":
        return <AcUnitIcon {...iconProps} />;
      case "hot":
        return <WhatshotIcon {...iconProps} />;
      case "top":
        return <TrendingUpIcon {...iconProps} />;
      case "down":
        return <TrendingDownIcon {...iconProps} />;
      case "operator":
        return <PersonIcon {...iconProps} />;
      case "table":
        return <TableBarIcon {...iconProps} />;
      default:
        return <RamenDiningIcon {...iconProps} />;
    }
  };

  const isClickable = !!action;

  return (
    <motion.div
      whileHover={isClickable ? "hover" : undefined}
      whileTap={isClickable ? "tap" : undefined}
      variants={isClickable ? cardVariants : undefined}
      onClick={isClickable ? action : undefined}
      style={{ height: "100%", cursor: isClickable ? "pointer" : "default" }}
    >
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          backgroundColor: theme.palette.background.paper,
          overflow: "hidden",
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          height: "100%",
          transition: "all 0.2s ease-in-out",
          "&:hover": isClickable
            ? {
                boxShadow: theme.shadows[2],
                borderColor: alpha(colors.main, 0.2),
              }
            : {},
        }}
      >
        {cardSize !== "small" && label && (
          <Box
            sx={{
              p: 2,
              backgroundColor: alpha(colors.main, 0.05),
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <motion.div variants={iconVariants} whileHover="hover">
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: colors.light,
                    color: colors.main,
                    borderRadius: 1.5,
                    p: 0.75,
                    width: 32,
                    height: 32,
                  }}
                >
                  {getIcon()}
                </Box>
              </motion.div>
              <Typography
                variant="body1"
                fontWeight="600"
                color={theme.palette.text.primary}
              >
                {label}
              </Typography>
            </Stack>
          </Box>
        )}

        <Box sx={{ p: 2, pt: 3 }}>
          <Typography
            variant={cardSize === "small" ? "h5" : "h3"}
            align="center"
            fontWeight="bold"
            sx={{
              color: colors.main,
              py: 1.5,
            }}
          >
            {value}
          </Typography>

          {secondaryValue && (
            <Chip
              label={secondaryValue}
              size="small"
              sx={{
                display: "block",
                mx: "auto",
                my: 1.5,
                fontWeight: "medium",
                backgroundColor: alpha(colors.main, 0.1),
                color: colors.main,
                borderRadius: 1,
              }}
            />
          )}

          {subValue > 0 && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="center"
              sx={{
                mt: 1.5,
                py: 0.5,
                px: 1.5,
                borderRadius: 1,
                backgroundColor: alpha(theme.palette.error.main, 0.05),
                width: "fit-content",
                mx: "auto",
              }}
            >
              <CancelIcon
                fontSize="small"
                sx={{ color: theme.palette.error.main, fontSize: 16 }}
              />
              <Typography
                variant="caption"
                fontWeight="bold"
                color={theme.palette.error.main}
              >
                {subValue} Cancelados
              </Typography>
            </Stack>
          )}

          {action && (
            <>
              <Divider sx={{ my: 2, opacity: 0.6 }} />
              <Box display="flex" justifyContent="center">
                <motion.div
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                >
                  <Button
                    variant="text"
                    endIcon={<ArrowRightAltIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      action();
                    }}
                    size="small"
                    sx={{
                      color: colors.main,
                      fontWeight: "medium",
                      "&:hover": {
                        backgroundColor: alpha(colors.main, 0.1),
                      },
                    }}
                  >
                    Ver Detalhes
                  </Button>
                </motion.div>
              </Box>
            </>
          )}
        </Box>
      </Paper>
    </motion.div>
  );
}
